/**
 * This component is a resolver. It is meant to be used by page templates using
 * data from a CMS.
 *
 * You can send the data you receive from the CMS to the props of this components
 * by spreading them (like <Teasers {...cmsData} />)
 * and then make these props fit the final module.
 */
import React from "react"
import { Section, CmsImage } from "@bw/bits"
import { Teasers as TeasersModule } from "@bw/modules"
import { Teaser } from "@bw/partials"
import { useStaticQuery, graphql } from "gatsby"
import { useTranslation } from "react-i18next"

const TeasersManual = ({
  suptitle,
  title,
  subtitle,
  button,
  buttonPosition,
  related,
  numberOfColumns,
}) => {
  const { t } = useTranslation()
  const data = useStaticQuery(graphql`
    query allItemsInCollection {
      posts: allCollectionArticles {
        nodes {
          id
          title
          url
          excerpt: teaserExcerpt
          image: teaserImage {
            localImage {
              childImageSharp {
                gatsbyImageData(width: 800, height: 450)
              }
            }
          }
        }
      }
      pages: allCollectionPages {
        nodes {
          id
          title
          url
          excerpt: teaserExcerpt
          image: teaserImage {
            localImage {
              childImageSharp {
                gatsbyImageData(width: 800, height: 450)
              }
            }
          }
        }
      }
    }
  `)
  const nodes = [...data.pages.nodes, ...data.posts.nodes]

  const ids = related.map(item => item.id)

  const items = nodes.filter(item => ids.includes(item.id))

  return (
    <Section {...{ suptitle, title, subtitle, button, buttonPosition }}>
      <TeasersModule numberOfColumns={parseInt(numberOfColumns.value)}>
        {items.map(({ title, excerpt, image, url }, i) => {
          return (
            <Teaser
              {...{ title, excerpt }}
              key={i}
              image={<CmsImage src={image} alt={title} />}
              to={url}
              buttonLabel={t("articles.button")}
            />
          )
        })}
      </TeasersModule>
    </Section>
  )
}

/**
 * This generates a GraphQL Fragment that can be used in a page query:
 *
 * Example:
 * ... on TeasersManualModule {
 *   ...TeasersManualModuleFragment
 * }
 *
 * This is also what you will get in the props of your {{pascalCase name}}
 * resolver component.
 */
export const query = graphql`
  fragment TeasersManualModuleFragment on TeasersManualModule {
    related {
      id
    }
    numberOfColumns {
      value
    }
  }
`

export { TeasersManual }
